import { useEffect, useState } from 'react';
import { GameData } from '@/features/games/shared/types/game-data';
import { useLocale, useTranslations } from 'next-intl';
import { GameImageDisplay } from '@/features/games/shared/components/game-image-display';
import { MessageBox } from '@/shared/components/message-box';
import { Button } from '@/shared/components/primitives/button';
import Loader from '@/shared/components/loader';
import { useMembershipTokenStore } from '@/features/membership/shared/state/membership-token-store';
import { useGameStore } from '@/features/games/shared/store/game-store';
import { getGameDownloadUrl } from '@/features/games/shared/helpers/get-game-download-url';
import { useIsWTGamesApp } from '@/features/games-app-bridge/shared/hooks/use-is-wt-games-app';
import { useModalContext } from '@/shared/components/overlay/use-modal-context';
import { useOverlayStore } from '@/shared/components/overlay/overlay.store';
import { Diamond } from 'lucide-react';
import { MembershipTokenAlreadyOwnedError } from '@/features/membership/shared/errors/membership-token-errors';
import { selectGamePrice } from '@/features/games/shared/store/game-price-slice-selectors';
import { mapGamePrices } from '@/features/games/shared/helpers/map-game-prices';

type Props = {
  gameData: GameData;
  tokenValue?: number;
};

export function PurchaseGameByTokenOverlay({ gameData }: Props) {
  const t = useTranslations('PurchaseGameByToken');
  const locale = useLocale();
  const isWTGamesApp = useIsWTGamesApp();
  const redeemTokens = useMembershipTokenStore((s) => s.redeemGameWithTokens);
  const fetchPurchaseHistory = useGameStore((s) => s.fetchPurchaseHistory);
  const bustPurchaseHistoryCache = useGameStore((s) => s.bustPurchaseHistoryCache);
  const prices = useGameStore(selectGamePrice(gameData.sku));
  const tokenPrice = prices && mapGamePrices(prices, true).tokens?.[0]?.value;
  const { setHeader } = useModalContext();
  const hideOverlay = useOverlayStore((s) => s.hideOverlay);

  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [orderId, setOrderId] = useState<number | string>('');

  useEffect(() => {
    setHeader(
      <h1 className='font-heading flex-1 px-4 text-center text-xl'>
        {success ? t('confirmRedeemSuccess') : t('confirmRedeem')}
      </h1>,
    );
  }, [setHeader, t, success]);

  const handleClick = async () => {
    setProcessing(true);
    setError(false);
    setErrorMessage(null);
    try {
      const data = await redeemTokens(gameData.sku, locale);
      setOrderId(data);
      setSuccess(true);
      bustPurchaseHistoryCache();
      await fetchPurchaseHistory();
    } catch (err) {
      console.error('Error while redeeming token:', err);
      if (err instanceof MembershipTokenAlreadyOwnedError) {
        setErrorMessage(t('alreadyOwnedErrorMessage'));
      } else {
        setErrorMessage(t('errorMessage'));
      }
      setError(true);
    } finally {
      setProcessing(false);
    }
  };

  const downloadGame = () => {
    const downloadUrl = getGameDownloadUrl(gameData.sku, isWTGamesApp);
    window.location.assign(downloadUrl);
  };

  return (
    <div className='flex gap-4 p-6'>
      <div className='flex flex-1 gap-4'>
        <div className='w-[300px]'>
          {gameData.defaultImage && (
            <GameImageDisplay image={gameData.defaultImage} imageSize='square' sizes='300px' />
          )}
        </div>

        <div className='flex flex-1 flex-col justify-between'>
          <div>
            {gameData.developer?.value && (
              <p className='text-sm text-gray-500'>{gameData.developer.value}</p>
            )}
            <h2 className='font-heading text-3xl'>{gameData.name}</h2>

            {success ? (
              <>
                <p className='mt-4'>
                  {t('successOrderDetails', {
                    orderId,
                  })}
                </p>
                <p className='mt-2 flex items-center gap-1'>
                  {t.rich('successTokenSpend', {
                    tokenValue: tokenPrice ?? 0,
                    icon: () => <Diamond aria-hidden className='text-primary h-4 w-4' />,
                  })}
                </p>
                <p className='mt-4'>{t('successMessage')}</p>
              </>
            ) : (
              <p className='mt-4'>{t('useTokens', { tokenValue: tokenPrice ?? '' })}</p>
            )}

            {error && !success && (
              <div role='alert' className='my-4'>
                <MessageBox type='error' className='w-full' closable={false}>
                  {errorMessage ?? t('errorMessage')}
                </MessageBox>
              </div>
            )}
          </div>

          <div className='mt-auto flex flex-wrap justify-end gap-3'>
            <Button
              onClick={success ? downloadGame : handleClick}
              disabled={processing}
              variant='primary'
              className='min-w-40'
            >
              {processing ? (
                <Loader label={t('loading')} />
              ) : success ? (
                t('download')
              ) : (
                t('redeemNow')
              )}
            </Button>
            {success && (
              <Button
                onClick={hideOverlay}
                disabled={processing}
                variant='primary'
                className='min-w-40'
              >
                {t('continueShopping')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
